import React, { useCallback } from 'react';
import { memo } from 'react';
import BaseNode from '../BaseNode';
import { Box, Typography, Chip, alpha, useTheme } from '@mui/material';
import FilterIcon from '@mui/icons-material/Filter';
import { 
  SliderControl,
  SelectControl,
  SwitchControl,
  ActivationSelector,
} from '../../core/NodeControls';

/**
 * Conv1DNode represents a 1D convolutional layer in the neural network
 */
const Conv1DNode = ({ data, id, selected, isConnectable }) => {
  const theme = useTheme();
  
  // Handler to update node data
  const handleDataChange = useCallback((key, value) => {
    if (data.onChange) {
      data.onChange(id, key, value);
    }
  }, [data, id]);
  
  // Generate preview content
  const renderPreview = () => {
    return (
      <>
        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1, textAlign: 'center' }}>
          Filters: {data.filters || 32}
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5 }}>
          <Chip 
            label={`kernel: ${data.kernel_size || 3}`}
            size="small"
            sx={{ 
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
            }}
          />
          
          {data.dilation_rate && data.dilation_rate > 1 && (
            <Chip 
              label={`dilation: ${data.dilation_rate}`}
              size="small"
              sx={{ 
                fontSize: '0.7rem',
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
              }}
            />
          )}
        </Box>
        
        {/* Visual representation of convolutional filter */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mt: 1,
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {/* Input */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Box 
                key={index}
                sx={{ 
                  width: 8,
                  height: 8,
                  backgroundColor: theme.palette.error.main,
                  opacity: 0.3,
                  borderRadius: 1,
                }} 
              />
            ))}
          </Box>
          
          {/* Filter representation */}
          <Box 
            sx={{ 
              height: 40, 
              width: 20, 
              border: `2px dashed ${alpha(theme.palette.error.main, 0.5)}`,
              borderRadius: 1,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: -15,
                width: 10,
                height: 10,
                transform: 'translateY(-50%) rotate(45deg)',
                borderTop: `2px solid ${alpha(theme.palette.error.main, 0.5)}`,
                borderRight: `2px solid ${alpha(theme.palette.error.main, 0.5)}`,
              }
            }} 
          />
          
          {/* Output */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Box 
                key={index}
                sx={{ 
                  width: 8,
                  height: 8,
                  backgroundColor: theme.palette.error.main,
                  opacity: 0.6,
                  borderRadius: 1,
                }} 
              />
            ))}
          </Box>
        </Box>
      </>
    );
  };
  
  // Controls for the node configuration popover
  const renderControls = () => {
    return (
      <>
        <SliderControl
          value={data.filters || 32}
          onChange={(value) => handleDataChange('filters', value)}
          min={1}
          max={256}
          step={1}
          label="Filters"
          tooltip="Number of output filters in the convolution"
          marks
        />
        
        <SliderControl
          value={data.kernel_size || 3}
          onChange={(value) => handleDataChange('kernel_size', value)}
          min={1}
          max={10}
          step={1}
          label="Kernel Size"
          tooltip="Size of the convolution window"
          marks
        />
        
        <SliderControl
          value={data.strides || 1}
          onChange={(value) => handleDataChange('strides', value)}
          min={1}
          max={5}
          step={1}
          label="Strides"
          tooltip="Stride length of the convolution"
          marks
        />
        
        <SelectControl
          value={data.padding || 'valid'}
          onChange={(value) => handleDataChange('padding', value)}
          options={[
            { value: 'valid', label: 'Valid' },
            { value: 'same', label: 'Same' },
            { value: 'causal', label: 'Causal' },
          ]}
          label="Padding"
          tooltip="Padding method"
        />
        
        <SliderControl
          value={data.dilation_rate || 1}
          onChange={(value) => handleDataChange('dilation_rate', value)}
          min={1}
          max={10}
          step={1}
          label="Dilation Rate"
          tooltip="Dilation rate for dilated convolution"
          marks
        />
        
        <ActivationSelector
          value={data.activation || 'relu'}
          onChange={(value) => handleDataChange('activation', value)}
          label="Activation"
        />
        
        <SwitchControl
          checked={data.use_bias !== false}
          onChange={(value) => handleDataChange('use_bias', value)}
          label="Use Bias"
          tooltip="Whether the layer uses a bias vector"
        />
      </>
    );
  };
  
  return (
    <BaseNode
      data={data}
      id={id}
      selected={selected}
      isConnectable={isConnectable}
      icon={<FilterIcon fontSize="small" />}
      nodeType="conv"
      preview={renderPreview()}
      controls={renderControls()}
      onDelete={data.onDelete}
    />
  );
};

export default memo(Conv1DNode); 